import { useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { format } from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import { Clock, Dumbbell, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import RecordingModeBadge from '../training/RecordingModeBadge';
import RunningWorkoutViz from '../training/RunningWorkoutViz';
import {
  getPlannerSplits,
  getPlannerTitle,
  hasLinkedStravaActivity,
  LINKED_STRAVA_ACTIVITY_META_KEY,
} from './plannerUtils';

function toTimeInputValue(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '08:00';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function applyTimeToDay(iso, dayKey, timeStr) {
  const [hh, mm] = (timeStr || '08:00').split(':').map((x) => parseInt(x, 10) || 0);
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

/** Match DndContext PointerSensor activation distance so a tap opens, a drag reorders. */
const TAP_MAX_PX = 8;

export default function PlannerPlannedChip({
  item,
  dayKey,
  onTimeChange,
  onEditStructure,
  onRequestDelete,
  onOpenLinkedActivity,
  busy = false,
}) {
  const { t, language } = useLanguage();
  const locale = language === 'no' ? nb : enUS;
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeDraft, setTimeDraft] = useState(() => toTimeInputValue(item.occurred_at));
  const mainPointerRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `planned-${item.id}`,
    data: {
      type: 'planned',
      item,
      dayKey,
    },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px,${transform.y}px,0)` }
    : undefined;

  const title = getPlannerTitle(item);
  const splits = getPlannerSplits(item);
  const canEdit = !!item.user_running_exercise_id;

  const saveTime = async () => {
    const next = applyTimeToDay(item.occurred_at, dayKey, timeDraft);
    await onTimeChange?.(item.id, next.toISOString());
    setTimeOpen(false);
  };

  const linkedActivity = hasLinkedStravaActivity(item);

  const handleMainActivate = () => {
    if (busy) return;
    if (linkedActivity && typeof onOpenLinkedActivity === 'function') {
      const id = Number(item.metadata?.[LINKED_STRAVA_ACTIVITY_META_KEY]);
      if (id > 0) onOpenLinkedActivity(id);
      return;
    }
    if (canEdit && typeof onEditStructure === 'function') {
      onEditStructure(item);
    }
  };

  const mainAria =
    linkedActivity && typeof onOpenLinkedActivity === 'function'
      ? t('trainingLog.plannerOpenLinkedActivity')
      : canEdit
        ? t('runningExercise.editPlan')
        : title || t('trainingLog.typePlannedSession');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-md border border-border bg-card p-1.5 text-card-foreground shadow-sm',
        isDragging && 'opacity-60 ring-2 ring-primary/30',
        busy && 'pointer-events-none opacity-70'
      )}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-start gap-1">
        <div
          className={cn(
            'min-w-0 flex-1 touch-none text-left',
            (linkedActivity || canEdit) && !busy && 'cursor-pointer select-none'
          )}
          role={linkedActivity || canEdit ? 'button' : undefined}
          tabIndex={linkedActivity || canEdit ? (busy ? -1 : 0) : undefined}
          aria-label={mainAria}
          onKeyDown={(e) => {
            if (busy || (!linkedActivity && !canEdit)) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            handleMainActivate();
          }}
          onPointerDown={(e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            mainPointerRef.current = { x: e.clientX, y: e.clientY };
          }}
          onPointerUp={(e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            const start = mainPointerRef.current;
            mainPointerRef.current = null;
            if (start == null || busy || isDragging) return;
            const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
            if (dist > TAP_MAX_PX) return;
            handleMainActivate();
          }}
          onPointerCancel={() => {
            mainPointerRef.current = null;
          }}
        >
          {splits.length > 0 && (
            <RunningWorkoutViz splits={splits} heightPx={20} className="mb-1" />
          )}
          <p className="line-clamp-2 flex items-center gap-0.5 text-[10px] font-medium leading-tight text-foreground">
            {!!linkedActivity && (
              <Dumbbell className="h-3 w-3 shrink-0 text-primary" aria-hidden title={t('trainingLog.plannerLinkedActivityHint')} />
            )}
            <span className="min-w-0">{title}</span>
            <RecordingModeBadge mode={item.user_running_exercise?.recording_mode} iconOnly />
          </p>
          <p className="mt-0.5 text-[9px] text-muted-foreground">
            {format(new Date(item.occurred_at), 'p', { locale })}
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-0.5" onPointerDown={(e) => e.stopPropagation()}>
          <Popover open={timeOpen} onOpenChange={setTimeOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                aria-label={t('trainingLog.plannerSetTime')}
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeDraft(toTimeInputValue(item.occurred_at));
                }}
              >
                <Clock className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="z-[10280] w-auto p-3"
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <Label className="text-xs">{t('trainingLog.plannerTimeLabel')}</Label>
              <form
                className="mt-2 flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void saveTime();
                }}
              >
                <Input
                  type="time"
                  value={timeDraft}
                  onChange={(e) => setTimeDraft(e.target.value)}
                  className={cn(
                    'citius-planner-time-input h-8 w-[7rem] text-xs dark:[color-scheme:dark]'
                  )}
                />
                <Button type="submit" size="sm" className="h-8">
                  {t('common.save')}
                </Button>
              </form>
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                aria-label={t('trainingLog.plannerMoreActions')}
              >
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[10280]">
              {canEdit && (
                <DropdownMenuItem
                  onClick={() => onEditStructure?.(item)}
                  className="text-xs"
                >
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  {t('runningExercise.editPlan')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onRequestDelete?.(item)}
                className="text-xs text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
